/**
 * Which projects a picker may offer.
 *
 * There are two project pickers, the sidebar's scope filter and the `/new`
 * composer's, and they used to answer this question in separate code over the
 * same sources. They disagreed: removing a cloud project took it out of the
 * sidebar and left it selectable at `/new`, where a turn could still be
 * dispatched into it (FOR-36359). The rules live here so a picker can only
 * disagree by not calling them.
 *
 * Not the same question as "which projects does the sidebar draw as rows". A
 * checkout whose directory is gone still gets a row, marked, so the user can
 * remove it. One rule, more than one projection.
 */

/** A project identity a catalog row can register. */
export interface RegistrableProjectIdentity {
  readonly environmentId: string;
  readonly projectId: string;
  /** Workspace path, when the project has one. Empty on a runtime-hosted row. */
  readonly cwd?: string | null;
}

/** The parts of a catalog row that answer "does this row register that project". */
export interface RegisteringCatalogRow {
  readonly environmentId: string | null;
  readonly workspaceRoot?: string | null;
  readonly resolvedRef?: { readonly projectId: string } | null;
}

/**
 * Whether the catalog PROVES this project is no longer registered.
 *
 * Deliberately asymmetric. "No row names it" is not the same as "it is gone",
 * and reading one as the other hides projects that are alive:
 *
 *  - No rows at all for its runtime: we cannot tell. A connected cloud project
 *    whose row the catalog cannot yet name is a state the composer is expected
 *    to work in, and several `/new` tests submit turns from exactly there, so
 *    reading "absent" as "removed" here would take working projects away. The
 *    sidebar IS stricter on this one case: it drops such a row rather than
 *    offer a scope filter it cannot label. That asymmetry is deliberate and
 *    lives here, in one file, rather than in two pipelines that drifted.
 *  - Exactly one row: that row is this project, whatever it says about itself.
 *    This is the same lone-row rule `resolveCheckoutRowForRegistration`
 *    already applies, and it is what keeps a connected project working while
 *    its row carries no resolved ref.
 *  - Several rows and none names it: the runtime is registered and this project
 *    is not one of the registrations. That is proof, and it is the case the
 *    user hit: a per-owner runtime hosts every one of that user's cloud
 *    projects, so a runtime-level match answers "is this user's machine
 *    registered", which stays true for a project whose registration was just
 *    deleted.
 *
 * The whole rule is guarded by {@link isCloudRegistrationLive}, so none of it
 * applies until the catalog has actually answered.
 */
export function catalogProvesProjectUnregistered(
  rows: readonly RegisteringCatalogRow[],
  project: RegistrableProjectIdentity,
): boolean {
  const onThisRuntime = rowsOnRuntime(rows, project.environmentId);
  if (onThisRuntime.length < 2) return false;
  // A row can only single a project out through a resolved ref or a workspace
  // path no sibling shares. Identity is established on connect, and a row's
  // workspace root is the SPRITE's rather than the project's, so on a
  // freshly-read per-owner runtime no row has either. Nothing there can be told
  // apart, so nothing there is proof, and reading it as proof would drop every
  // one of that user's cloud projects at once.
  if (discriminatingRows(onThisRuntime).length === 0) return false;
  return findRegisteringCatalogRow(rows, project) === undefined;
}

/**
 * The catalog row that registers this project, if the catalog can name one.
 *
 * The single matcher both pickers use. Undefined means the catalog does not
 * name it, which only amounts to proof of removal under the conditions
 * {@link catalogProvesProjectUnregistered} spells out.
 */
export function findRegisteringCatalogRow<TRow extends RegisteringCatalogRow>(
  rows: readonly TRow[],
  project: RegistrableProjectIdentity,
): TRow | undefined {
  const named = findNamingCatalogRow(rows, project);
  if (named) return named;
  const onThisRuntime = rowsOnRuntime(rows, project.environmentId);
  const discriminating = discriminatingRows(onThisRuntime);
  // A lone row is this project, whatever it says about itself, and a runtime
  // whose rows can single nobody out cannot exclude anybody either. Both fall
  // back to the first row rather than leaving the project unregistered.
  return onThisRuntime.length === 1 || discriminating.length === 0 ? onThisRuntime[0] : undefined;
}

/**
 * The catalog row that NAMES this project, with no fallback.
 *
 * Separate from {@link findRegisteringCatalogRow} because the two answer
 * different questions and only one of them may be guessed at. "Is this project
 * still registered" tolerates a fallback: a lone row, or a runtime nobody can
 * be singled out on, keeps the project rather than dropping it. "Which
 * registration IS this project" tolerates nothing, because removal's `locate()`
 * treats a registration id as deciding on its own, so a project wearing a
 * sibling's id would unregister the wrong project.
 */
export function findNamingCatalogRow<TRow extends RegisteringCatalogRow>(
  rows: readonly TRow[],
  project: RegistrableProjectIdentity,
): TRow | undefined {
  return discriminatingRows(rowsOnRuntime(rows, project.environmentId)).find(
    (row) =>
      row.resolvedRef?.projectId === project.projectId ||
      ((project.cwd ?? "").length > 0 && row.workspaceRoot === project.cwd),
  );
}

/**
 * The rows that can single a project out from their siblings on one runtime.
 *
 * A resolved ref names its project outright. A workspace path only names one if
 * no sibling reports the same path: a row's `workspaceRoot` is the SPRITE root
 * rather than the project directory, so on a per-owner runtime every row
 * reports the same one and a shared path singles nobody out (FOR-36358). An
 * empty path is the same story with nothing in it at all.
 */
function discriminatingRows<TRow extends RegisteringCatalogRow>(
  onThisRuntime: readonly TRow[],
): readonly TRow[] {
  const rootCounts = new Map<string, number>();
  for (const row of onThisRuntime) {
    const root = row.workspaceRoot ?? "";
    if (root.length > 0) rootCounts.set(root, (rootCounts.get(root) ?? 0) + 1);
  }
  return onThisRuntime.filter((row) => {
    if (row.resolvedRef !== null && row.resolvedRef !== undefined) return true;
    const root = row.workspaceRoot ?? "";
    return root.length > 0 && rootCounts.get(root) === 1;
  });
}

function rowsOnRuntime<TRow extends RegisteringCatalogRow>(
  rows: readonly TRow[],
  environmentId: string,
): readonly TRow[] {
  return rows.filter((row) => row.environmentId === environmentId);
}

/**
 * Whether a runtime-hosted project still has a live cloud registration.
 *
 * A catalog that answered cleanly is authoritative: connected runtime state
 * cannot bring a project back after its registration was removed. When the
 * catalog could not be read the connected rows stand, because "we could not
 * ask" must not read as "it is gone".
 */
export function isCloudRegistrationLive(input: {
  readonly catalogAuthoritative: boolean;
  readonly provenUnregistered: boolean;
}): boolean {
  return !input.catalogAuthoritative || !input.provenUnregistered;
}

/**
 * Whether a picker may offer this project.
 *
 * An archived board is not somewhere work continues, and a project whose cloud
 * registration is gone is not somewhere work can start. Local projects are
 * unaffected by the catalog: nothing registers them, so nothing can unregister
 * them either.
 */
export function isPickableProject(input: {
  readonly runtimeHosted: boolean;
  readonly catalogAuthoritative: boolean;
  readonly provenUnregistered: boolean;
  readonly cmmdProjectId: number | null;
  readonly archivedCmmdProjectIds: ReadonlySet<number>;
}): boolean {
  if (input.cmmdProjectId !== null && input.archivedCmmdProjectIds.has(input.cmmdProjectId)) {
    return false;
  }
  if (!input.runtimeHosted) return true;
  return isCloudRegistrationLive(input);
}

/**
 * Whether a turn may be STARTED in this project, given it is pickable.
 *
 * Deliberately separate. `availability` is a server probe of whether the
 * project's `cwd` still exists, and only an explicit `"missing"` disqualifies
 * it: `undefined` and `"unknown"` mean the probe never ran, and treating those
 * as missing would empty a picker on any snapshot predating it.
 *
 * Without this a project whose directory was deleted stayed selectable, because
 * the browser's persisted project order outlives the directory. In production
 * that offered four phantom "E2E" rows, resolved the default to one of them,
 * and dispatched turns into a path that no longer existed, where they hung with
 * no visible error.
 */
export function canStartWorkInProject(project: {
  readonly availability?: string | null | undefined;
}): boolean {
  return project.availability !== "missing";
}
